import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from '@/store'
import VueBulma from '@billow/vue-bulma'
import { filters, stores, mixins } from '@billow/js-helpers'
import EasyRoute from '@billow/easy-route'
import * as guards from './router/guards'
import Toast from '@billow/toast'
import * as feather from '@billow/vue-bulma/icons/feather-icons'
import * as zondicons from '@billow/vue-bulma/icons/zondicons'
import Croppa from 'vue-croppa'
import 'vue-croppa/dist/vue-croppa.css'
import Closeable from './directives/Closeable'
import ClickOutside from './directives/click-outside'
import Markdown from 'vue-markdown-it'
import VueProgressiveImage from 'vue-progressive-image'
// import VueApexCharts from 'vue-apexcharts'

Vue.use(VueProgressiveImage)

Vue.use(VueBulma, {
  store,
  config: {
    components: {
      fieldLabel: {
        hintIconPack: 'feather',
        hintIcon: 'info',
      },
      actionButton: {
        dropdownIconPack: 'feather',
        leftIconPack: 'feather',
        dropdownIcon: 'chevron-down'
      },
      pager: {
        buttonIconPack: 'feather',
        nextButtonIcon: 'chevron-right',
        prevButtonIcon: 'chevron-left',
        lastButtonIcon: 'chevrons-right',
        firstButtonIcon: 'chevrons-left'
      },
    },
    icons: {
      pack: 'feather',
      feather,
      zondicons
    }
  }
})
Vue.use(filters)
Vue.prototype.$filters = Vue.options.filters
Vue.use(stores, { store })

Vue.use(EasyRoute, {
  router,
  guards: [
    guards.auth,
    guards.customer,
    guards.guest,
    guards.passwordSet,
    guards.accountIsSetup,
    guards.userIsSetup
  ]
})

Vue.use(Toast, {
  namespaced: true,
  types: [...Toast.types]
})

Vue.use(Croppa)

Vue.directive('closeable', Closeable)
Vue.directive('click-outside', ClickOutside)

Vue.component('markdown', Markdown)

// Transitions
Vue.component('expand-collapse', () => import('@/transitions/ExpandCollapse'))

// Partials
Vue.component('tag', () => import('@/partials/Tag'))
Vue.component('page', () => import('@/partials/Page'))
Vue.component('loader', () => import('@/partials/Loader'))
Vue.component('level', () => import('@/partials/Level'))
Vue.component('route', () => import('@/partials/Route'))
Vue.component('nav-route', () => import('@/partials/NavRoute'))
Vue.component('line-stat', () => import('@/partials/LineStat'))
Vue.component('markdown-container', () => import('@/partials/MarkdownContainer'))
Vue.component('reactions', () => import('@/partials/Reactions'))
Vue.component('reaction-toolbar', () => import('@/partials/ReactionToolbar'))
Vue.component('no-data', () => import('@/partials/NoData'))

Vue.component('markdown-editor', () => import('@/views/markdown/Editor'))
Vue.component('dashboard-admin', () => import('@/views/home/Admin'))
Vue.component('dashboard-user', () => import('@/views/home/CompanyUser'))
Vue.component('dashboard-supplier-user', () => import('@/views/home/CompanySupplier'))
Vue.component('dashboard-client-user', () => import('@/views/home/CompanyClient'))
Vue.component('project-tools-collaborators', () => import('@/views/project/partials/Collaborators'))
Vue.component('project-tools-general', () => import('@/views/project/partials/GeneralTools'))
Vue.component('project-tools-actions', () => import('@/views/project/partials/Actions'))
Vue.component('project-tools-reports', () => import('@/views/project/partials/ReportTools'))
Vue.component('project-tools-notifications', () => import('@/views/project/partials/Notifications'))
Vue.component('task-progress', () => import('@/views/project/partials/TaskProgress'))
Vue.component('task-list', () => import('@/views/project/partials/tasks/List'))
Vue.component('general-project-table', () => import('@/views/project/partials/GeneralProjectTable'))
Vue.component('image-manager', () => import('@/partials/ImageManager'))
Vue.component('task-comment', () => import('@/views/task/partials/Comment'))
Vue.component('doughnut-chart', () => import('@/graphs/doughnut'))
Vue.component('todo-list', () => import('@/views/task/todo/List'))
Vue.component('todo-item', () => import('@/views/task/todo/Item'))
Vue.component('task-assign-user', () => import('@/views/task/AssignUser'))
Vue.component('task-assignee', () => import('@/views/task/Assignee'))
Vue.component('user-status', () => import('@/partials/UserStatus'))
Vue.component('only-premium-can', () => import('@/partials/upgrade/OnlyPremiumCanDoThat'))

// Workflow
Vue.component('tab-workflow-tasks', () => import('@/views/workflow/tabs/WorkflowTasks'))
Vue.component('tab-opened-tasks', () => import('@/views/workflow/tabs/OpenedTasks'))
Vue.component('tab-assigned-tasks', () => import('@/views/workflow/tabs/AssignedTasks'))

// Vue.component('apexchart', VueApexCharts)

Vue.config.productionTip = false

router.onError(error => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    localStorage.setItem('needs_refresh', '1')
    store.commit('app/needsRefresh')
  }
})

const vueConfig = {
  router,
  store,
  mixins: [mixins.errors],
  render: h => h(App),
  beforeMount() {
    this.$store.dispatch('auth/checkAuth')
  },

  methods: {
    async hasPermission(action, entityType, silent = false) {
      return await this.$store.dispatch('ability/hasPermission', { action, entityType, abilities: this.abilities })
        .then(() => true)
        .catch(error => {
          if (silent === false) {
            this.$toast.warning(error)
          }
    
      })
    },
    async doesNotHavePermission(action, entityType) {
      return await this.$store.dispatch('ability/doesNotHavePermission', { action, entityType, abilities: this.abilities })
      .then((response) => {
        this.$toast.warning(response)
        return true;
      }).catch(() => false)
    }
  },

  computed: {
    app() {
      return this.$children[0]
    },
    hasSubscription() {
      return this.$store.getters['auth/subscription']
    },
    subscriptionIsActive() {
      return this.$store.getters['auth/subscriptionIsActive']
    },
    isPayingPremium() {
      return this.$store.getters['auth/isPayingPremium']
    },
    abilities() {
      return this.$store.getters['auth/abilities']
    },
  }
}

let boot = async () => {

  Promise.all([
    store.dispatch('auth/loadUser'),
    store.dispatch('company/loadActiveCompany'),
    store.dispatch('notification/fetchNotifications'),
  ]).then(() => {
    new Vue(vueConfig).$mount('#app')
  }).catch(() => {
    new Vue(vueConfig).$mount('#app')
  })


}

boot()
